import {
  APPOINTMENT_STATUSES,
  canTransitionAppointmentStatus,
  type AppointmentStatus,
} from '@emr/shared';

/**
 * Which status buttons the schedule UI offers, and what each one is labelled.
 *
 * `claude-kit/rules/invariants.md`'s "Business rules live in pure, tested modules" — the actual rule
 * about which transitions are legal already lives in `@emr/shared`'s `canTransitionAppointmentStatus`
 * (`shared/src/schedulingRequests.ts`), and per the task brief this UI must only ever offer a
 * transition that function permits, never a hand-maintained list of its own. This module derives the
 * offered set from that one function rather than re-deriving it, and adds only the presentation layer
 * (labels, ordering) on top.
 */

export const STATUS_LABELS: Record<AppointmentStatus, string> = {
  scheduled: 'Scheduled',
  checked_in: 'Checked in',
  completed: 'Completed',
  cancelled: 'Cancelled',
  no_show: 'No-show',
};

/** The verb for the button that performs the transition *to* this status — distinct from the noun
 * used to describe a status once reached (`STATUS_LABELS`). */
const TRANSITION_ACTION_LABELS: Record<AppointmentStatus, string> = {
  scheduled: 'Reopen', // Never actually offered — `scheduled` has no transition into it — kept so
  // the record type stays total over `AppointmentStatus` rather than partial.
  checked_in: 'Check in',
  completed: 'Mark completed',
  cancelled: 'Cancel',
  no_show: 'Mark no-show',
};

export interface StatusAction {
  readonly status: AppointmentStatus;
  readonly label: string;
}

/** A semantic colour role, not a Tailwind class — each renderer (`AppointmentList`'s text colour,
 * `CalendarGrid`'s block accent border) turns this into its own utility class, so the two share one
 * status→colour decision instead of maintaining two independent mappings that could drift apart. */
export type StatusTone = 'muted' | 'accent' | 'success' | 'danger' | 'warning';

const STATUS_TONES: Record<AppointmentStatus, StatusTone> = {
  scheduled: 'muted',
  checked_in: 'accent',
  completed: 'success',
  cancelled: 'danger',
  no_show: 'warning',
};

/** Which semantic colour a status renders in. */
export function statusTone(status: AppointmentStatus): StatusTone {
  return STATUS_TONES[status];
}

/** Every legal next status from `current`, in `APPOINTMENT_STATUSES`' own declared order, each with
 * the button label to show for it. Empty for a terminal status (`completed`, `cancelled`, `no_show`). */
export function availableStatusActions(current: AppointmentStatus): readonly StatusAction[] {
  return APPOINTMENT_STATUSES.filter((candidate) =>
    canTransitionAppointmentStatus(current, candidate),
  ).map((status) => ({ status, label: TRANSITION_ACTION_LABELS[status] }));
}
